/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

/**
 * The `FootprintIconProps` interface defines the properties for the FootprintIcon component.
 */
interface FootprintIconProps {
    /** CSS class to override default styling. */
    className?: string;
    /** Primary color of the icon. */
    color1?: string;
    /** Height of the icon. */
    height?: number | string;
    ref?: RefObject<SVGSVGElement>;
    /** Test ID for Cypress. */
    testId?: string;
    /** Width of the icon. */
    width?: number | string;
}

/**
 * Material Symbol "footprint" — outlined, weight 300.
 * Footprint icon for the Spurensuche (trace-hunting) category.
 *
 * @param props           The component props.
 * @param props.className CSS class override.
 * @param props.color1    Primary icon color.
 * @param props.height    Icon height.
 * @param props.ref       SVG element ref.
 * @param props.testId    Test identifier.
 * @param props.width     Icon width.
 * @returns A React SVG element.
 */
export const FootprintIcon = ({
    className = '',
    color1 = 'currentColor',
    height,
    ref,
    testId = 'FootprintIcon',
    width
}: FootprintIconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill={color1}
        height={height}
        viewBox="0 -960 960 960"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M260-840q-43.85 0-71.92 48.85Q160-742.31 160-680q0 60.31 18.08 112.65 18.07 52.35 39.23 82.89l98.07-21.62q13.77-32 29.2-78.96Q360-632 360-680q0-62.31-28.08-111.15Q303.85-840 260-840Zm55 520q19.38 0 32.19-14.38Q360-348.77 360-373q0-17-8.58-36.92-8.57-19.93-20.42-38.54l-91 20.77q0 46.15 18.46 76.92Q276.92-320 315-320Zm385-320q-43.85 0-71.92 48.85Q600-542.31 600-480q0 48 15.62 94.46 15.61 46.46 29 79.46l96.53 23.93q25.39-39.77 42.12-87.39Q800-417.15 800-480q0-62.31-28.08-111.15Q743.85-640 700-640Zm-55 520q37.31 0 56.15-30.38Q720-180.77 720-225l-90.61-23.85q-9.16 16.31-19.27 36.43Q600-192.31 600-173q0 20.38 12.88 36.69Q625.77-120 645-120ZM315-260q-65.46 0-101.04-49.69-35.57-49.7-33.96-117.62l-21.08-35.84q-15.61-24.31-37.26-82.77Q100-604.39 100-680q0-94.54 45.42-157.27Q190.85-900 260-900q73.08 0 116.54 67.42Q420-765.15 420-680q0 55.31-16.19 105.65-16.2 50.35-29.73 82.27l13 21.47q9.92 16.69 21.42 43.92Q420-399.46 420-373q0 48.54-29.73 80.77T315-260ZM645-60q-45.54 0-75.27-32.23Q540-124.46 540-173q0-26.46 11.5-53.69 11.5-27.23 21.42-43.92l13-21.47q-13.15-31.54-29.53-82.07Q540-424.69 540-480q0-85.15 43.46-152.58Q626.92-700 700-700q69.15 0 114.58 62.73Q860-574.54 860-480q0 75.61-21.46 133.38-21.46 57.77-37.46 82.85l-21.08 33q.61 62.15-31.96 116.46Q715.46-60 645-60Z" />
    </svg>
);

FootprintIcon.displayName = 'FootprintIcon';